const urljoin = require('url-join');
const configConstants = require('../config-constants');

const ROOT_DOMAIN = 'utrsports.net';

// Engage API has one shared instance per tier, not one per environment, and the App Service
// names do not follow a single pattern -- only the dev instance carries the `eastus` segment.
const ENGAGE_API_HOSTS = {
    dev: 'https://dev-utr-engage-api-eastus-data-azapp.azurewebsites.net/',
    qa: 'https://qa-utr-engage-api-data-azapp.azurewebsites.net/',
    prod: 'https://prod-utr-engage-api-data-azapp.azurewebsites.net/',
};

const nextAppUrl = (env = 'prod') => `https://${env}-new-app.utrsports.net`;

function toShortEnv(environmentName) {
    const env = String(environmentName).toLowerCase();
    if (/^test[1-6]$/.test(env)) {
        return 'dev';
    }
    if (env === 'qa' || env === 'uat') {
        return 'qa';
    }
    return env;
}

function getSearchApiHost(environmentName) {
    if (process.env.SEARCHAPIHOST) {
        return process.env.SEARCHAPIHOST;
    }
    const env = String(environmentName).toLowerCase();
    if (env === 'prod' || env === 'production') {
        return `https://api.${ROOT_DOMAIN}`;
    }
    if (env === 'qa') {
        return `https://qa-search-api.${ROOT_DOMAIN}`;
    }
    if (env === 'uat') {
        return `https://uat-search-api.${ROOT_DOMAIN}`;
    }
    return `https://dev-search-api.${ROOT_DOMAIN}`;
}

function deriveLocalHosts() {
    return {
        apiHost: `https://test1-api.${ROOT_DOMAIN}`,
        appHost: 'https://localhost:3443',
        appHostName: 'localhost',
        cdnUrl: `https://ci-cdn.${ROOT_DOMAIN}`,
        engageApiHost: 'http://localhost:24241',
        groupsApiHost: `https://test10-groups-api.${ROOT_DOMAIN}`,
        leaguesApiHost: `https://test1-leagues-api.${ROOT_DOMAIN}`,
        leaguesAppHost: 'https://localhost:3443',
        leaguesAppHostName: 'localhost',
        leaguesSearchApiHost: `https://dev-search-api.${ROOT_DOMAIN}`,
        searchApiHost: `https://dev-search-api.${ROOT_DOMAIN}`,
        utrStatsApiHost: 'https://dev-utr-volumetric-stats-eastus-data-azapp.azurewebsites.net/',
        performanceStatsApiHost: 'https://localhost:7157/',
        liveScoresApiHost: 'https://dev-utr-ptt-livescores-eastus-data-func.azurewebsites.net/api',
        marketingAppHost: 'http://localhost:3001',
        messagingApiHost: '',
        nextAppHost: 'http://localhost:3003',
        publicPath: '/',
        rankingsApiHost: `https://dev-rankings-api.${ROOT_DOMAIN}`,
        rostersApiHost: `https://dev-rosters-api.${ROOT_DOMAIN}`,
        stripeHost: 'https://connect.stripe.com',
        userSiteHost: 'https://localhost:44300',
        userSiteHostName: 'localhost',
    };
}

function deriveTestHosts(meta) {
    const environmentName = meta.name;
    const shortEnv = toShortEnv(environmentName);
    const environmentPrefix = environmentName === 'qa' ? 'qa' : 'dev';
    const cdnUrl = process.env.CDN_URL;
    const cdnUrlStatic = process.env.CDN_URL_STATIC;

    const apiUri = process.env.APIHOST || `https://${environmentName}-api.${ROOT_DOMAIN}`;
    const defaultLeaguesApiUri = process.env.LEAGUESAPIHOST || `https://${environmentName}-leagues-api.${ROOT_DOMAIN}`;
    const leaguesApiUri = meta.useQaLeagues
        ? (process.env.LEAGUESAPIHOST || `https://qa-leagues-api.${ROOT_DOMAIN}`)
        : defaultLeaguesApiUri;
    const groupsApiUri = process.env.GROUPSAPIHOST || `https://${environmentName}-groups-api.${ROOT_DOMAIN}`;
    const searchApiUri = getSearchApiHost(environmentName);
    const leaguesSearchApiUri = searchApiUri;
    const utrStatsApiUri = process.env.UTRSTATSAPIHOST || `https://${shortEnv}-utr-volumetric-stats-eastus-data-azapp.azurewebsites.net/`;
    const performanceStatsApiUri = process.env.PERFORMANCESTATSAPIHOST || `https://${shortEnv}-utr-performance-stats-eastus-data-azapp.azurewebsites.net/`;
    const liveScoresApiUri = process.env.LIVESCORESAPIHOST || `https://${shortEnv}-utr-ptt-livescores-eastus-data-func.azurewebsites.net/api`;
    const engageApiUri = process.env.ENGAGEAPIHOST || ENGAGE_API_HOSTS[shortEnv] || ENGAGE_API_HOSTS.dev;
    const rankingsApiUri = process.env.RANKINGSAPIHOST || `https://${environmentPrefix}-rankings-api.${ROOT_DOMAIN}`;
    const rostersApiUri = process.env.ROSTERSAPIHOST || `https://${environmentPrefix}-rosters-api.${ROOT_DOMAIN}`;

    return {
        apiHost: apiUri,
        appHost: process.env.APPHOST || `https://${environmentName}-app.${ROOT_DOMAIN}`,
        appHostName: process.env.APPHOSTNAME || `https://${environmentName}-app.${ROOT_DOMAIN}`,
        cdnUrl,
        engageApiHost: engageApiUri,
        groupsApiHost: groupsApiUri,
        leaguesApiHost: leaguesApiUri,
        leaguesAppHost: process.env.LEAGUES_APP_HOST || `https://${environmentName}-leagues.${ROOT_DOMAIN}`,
        leaguesAppHostName: process.env.LEAGUES_APP_HOST_NAME || (meta.useQaLeagues ? `qa-leagues.${ROOT_DOMAIN}` : `${environmentName}-leagues.${ROOT_DOMAIN}`),
        leaguesSearchApiHost: leaguesSearchApiUri,
        searchApiHost: searchApiUri,
        utrStatsApiHost: utrStatsApiUri,
        performanceStatsApiHost: performanceStatsApiUri,
        liveScoresApiHost: liveScoresApiUri,
        marketingAppHost: process.env.MARKETINGAPPHOST || `https://${ROOT_DOMAIN}`,
        messagingApiHost: `https://${shortEnv}-utr-messaging-api-eastus-platform-azapp.azurewebsites.net`,
        nextAppHost: nextAppUrl(environmentName),
        publicPath: cdnUrlStatic ? urljoin(cdnUrlStatic, configConstants.outputPath, configConstants.appCodePath) : '/',
        rankingsApiHost: rankingsApiUri,
        rostersApiHost: rostersApiUri,
        stripeHost: 'https://connect.stripe.com',
        userSiteHost: process.env.USERSITEHOST || `https://${environmentName}-app.${ROOT_DOMAIN}`,
        userSiteHostName: process.env.USERSITEHOST || `${environmentName}-app.${ROOT_DOMAIN}`,
    };
}

function deriveProdHosts() {
    const prodApiUri = process.env.APIHOST || `https://api.${ROOT_DOMAIN}`;
    const prodLeaguesApiUri = process.env.LEAGUESAPIHOST || `https://leagues-api.${ROOT_DOMAIN}`;
    const prodGroupsApiUri = process.env.GROUPSAPIHOST || `https://groups-api.${ROOT_DOMAIN}`;
    const prodSearchApiUri = getSearchApiHost('prod');
    const prodLeaguesSearchApiUri = prodSearchApiUri;
    const prodUtrStatsApiUri = process.env.UTRSTATSAPIHOST || `https://api.${ROOT_DOMAIN}`;
    // Intentionally mirrors prodUtrStatsApiUri. Before the power_performance_stats gate is
    // enabled in prod, platform must either set PERFORMANCESTATSAPIHOST or confirm Front Door
    // path-routes performance-stats requests from the api host.
    const prodPerformanceStatsApiUri = process.env.PERFORMANCESTATSAPIHOST || `https://api.${ROOT_DOMAIN}`;
    const prodLiveScoresApiUri = process.env.LIVESCORESAPIHOST || `https://prod-utr-ptt-livescores-eastus-data-func.azurewebsites.net/api`;
    const prodEngageApiUri = process.env.ENGAGEAPIHOST || ENGAGE_API_HOSTS.prod;
    const prodCdnUrl = process.env.CDN_URL || `https://prod-cdn.${ROOT_DOMAIN}`;
    const prodCdnStaticUrl = process.env.CDN_URL_STATIC || `https://prod-cdn-static.${ROOT_DOMAIN}`;

    return {
        apiHost: prodApiUri,
        appHost: process.env.APPHOST || `https://app.${ROOT_DOMAIN}`,
        appHostName: process.env.APPHOSTNAME || `app.${ROOT_DOMAIN}`,
        cdnUrl: prodCdnUrl,
        engageApiHost: prodEngageApiUri,
        groupsApiHost: prodGroupsApiUri,
        leaguesApiHost: prodLeaguesApiUri,
        leaguesAppHost: process.env.LEAGUES_APP_HOST || `https://leagues.${ROOT_DOMAIN}`,
        leaguesAppHostName: process.env.LEAGUES_APP_HOST_NAME || `leagues.${ROOT_DOMAIN}`,
        leaguesSearchApiHost: prodLeaguesSearchApiUri,
        searchApiHost: prodSearchApiUri,
        utrStatsApiHost: prodUtrStatsApiUri,
        performanceStatsApiHost: prodPerformanceStatsApiUri,
        liveScoresApiHost: prodLiveScoresApiUri,
        marketingAppHost: process.env.MARKETINGAPPHOST || `https://${ROOT_DOMAIN}`,
        messagingApiHost: prodApiUri, // in Prod this goes through FD and routed by path to the backend pools. So this stays prodApi
        nextAppHost: process.env.APPHOST || `https://app.${ROOT_DOMAIN}`,
        publicPath: urljoin(prodCdnStaticUrl, configConstants.outputPath, configConstants.appCodePath),
        rankingsApiHost: prodApiUri, // in Prod this goes through FD and routed by path to the backend pools. So this stays prodApi
        rostersApiHost: prodApiUri, // in Prod this goes through FD and routed by path to the backend pools. So this stays prodApi
        stripeHost: 'https://connect.stripe.com',
        userSiteHost: process.env.USERSITEHOST || `https://app.${ROOT_DOMAIN}`,
        userSiteHostName: process.env.USERSITEHOST || `app.${ROOT_DOMAIN}`,
    };
}

function deriveHosts(meta) {
    if (meta.type === 'local') {
        return deriveLocalHosts();
    }

    if (meta.type === 'test') {
        return deriveTestHosts(meta);
    }

    return deriveProdHosts();
}

module.exports = { deriveHosts, getSearchApiHost };
